
import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Sparkles, Wand2, Plus, AlertCircle, X, Play, Pencil, Check, Eye, Trash2, FileText, Lock, Coins, Settings2, Info, FastForward, StopCircle, RefreshCw, Zap, Layers, Download } from 'lucide-react';
import { rotateImage } from './utils/imageUtils';
import { ImageUploader } from './components/ImageUploader';
import { Button } from './components/Button';
import { ComparisonView } from './components/ComparisonView';
import { editImageWithGemini } from './services/geminiService';
import { AppStatus, ProcessedImage, ProcessingError, WorkflowStep, ModelId } from './types';
import { PRESET_PROMPTS, MODELS } from './constants';
import './styles.css';

const PRICE_IMAGE_OUTPUT_TEXT = 0.60; // Keep generic text price constant for now

// --- UTILS ---
// (Client-side utils removed as part of cleanup)

const App: React.FC = () => {
  const [status, setStatus] = useState<AppStatus>(AppStatus.IDLE);
  const [image, setImage] = useState<ProcessedImage | null>(null);
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([]);
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [error, setError] = useState<ProcessingError | null>(null);

  // UI State for editing prompts
  const [editingStepId, setEditingStepId] = useState<string | null>(null);
  const [editPromptValue, setEditPromptValue] = useState<string>('');

  // UI State for viewing history
  const [viewingStepId, setViewingStepId] = useState<string | null>(null);

  // UI State for stats visibility
  const [showCost, setShowCost] = useState<boolean>(false);

  // New State: Full Production Mode Selection
  // New State: Full Production Mode Selection (Removed)

  // Ref to scroll to bottom of queue when adding steps
  const queueEndRef = useRef<HTMLDivElement>(null);

  // Helper to calculate cost for a single step based on the model used
  const getStepCost = (step: WorkflowStep) => {
    // Client-side operations are free
    if (step.status === 'skipped') return 0;

    if (!step.usage && step.status !== 'complete') return null;

    // Default to Flash pricing if no model selected (legacy steps)
    const modelId = step.selectedModel || 'gemini-2.5-flash-image';
    const pricing = MODELS[modelId];

    if (!pricing) return 0;

    if (!step.usage) return 0;

    const inputCost = (step.usage.promptTokens / 1_000_000) * pricing.priceInput;

    if (step.outputType === 'text') {
      const outputCost = (step.usage.candidateTokens / 1_000_000) * PRICE_IMAGE_OUTPUT_TEXT;
      return inputCost + outputCost;
    } else {
      // Use the updated per-image cost from constants
      const outputCost = pricing.priceOutput;
      return inputCost + outputCost;
    }
  };

  // Stats Calculation
  const stats = useMemo(() => {
    let totalPromptTokens = 0;
    let totalCandidateTokens = 0;
    let totalCostVal = 0;

    workflowSteps.forEach(step => {
      if (step.usage) {
        totalPromptTokens += step.usage.promptTokens;
        totalCandidateTokens += step.usage.candidateTokens;
      }

      const stepCost = getStepCost(step);
      if (stepCost !== null) {
        totalCostVal += stepCost;
      }
    });

    return {
      totalTokens: totalPromptTokens + totalCandidateTokens,
      totalPromptTokens,
      totalCandidateTokens,
      totalCost: totalCostVal === 0 ? "$0.00" : `$${totalCostVal.toFixed(4)} `
    };
  }, [workflowSteps]);

  const handleImageSelected = useCallback((base64: string, mimeType: string, previewUrl: string) => {
    setImage({
      originalUrl: previewUrl,
      processedUrl: null,
      mimeType,
      originalBase64: base64
    });
    // Don't auto-set uploading status, just let it be ready
    setStatus(AppStatus.IDLE);
    setError(null);
    setWorkflowSteps([]);
    setViewingStepId(null);
  }, []);

  const addStep = (prompt: string, label: string, type: 'preset' | 'custom', options: Partial<WorkflowStep> = {}) => {
    const newStep: WorkflowStep = {
      id: crypto.randomUUID(),
      type,
      label,
      prompt,
      status: 'pending',
      resultUrl: null,
      outputType: 'image', // default
      readOnly: false,     // default
      selectedModel: 'gemini-2.5-flash-image', // Default model
      operation: 'gemini', // Default operation
      config: {},
      ...options
    };
    setWorkflowSteps(prev => [...prev, newStep]);

    // Scroll to bottom after state update
    setTimeout(() => {
      queueEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const changeStepModel = (stepId: string, modelId: ModelId) => {
    setWorkflowSteps(prev => prev.map(s =>
      s.id === stepId ? { ...s, selectedModel: modelId } : s
    ));
  };

  const updateStepConfig = (stepId: string, key: string, value: any) => {
    setWorkflowSteps(prev => prev.map(s =>
      s.id === stepId ? { ...s, config: { ...s.config, [key]: value } } : s
    ));
  };

  // --- SMART ENHANCEMENT HANDLER ---
  const handleAddSmartEnhancement = () => {
    // Step 1: Deep Visual & Defect Analysis (Gemini 3.0 Flash Preview)
    addStep(
      `Role: Expert Image Analyst & Quality Assurance Specialist.
  TASK: Analyze this image for defects and visual characteristics to guide a high - fidelity restoration.
       
       OUTPUT REQUIREMENTS:
1. DEFECTS: List specific issues(e.g., "Compression artifacts in background", "Motion blur on face", "Low contrast", "Noise").
       2. SUBJECT FIDELITY: Describe the subject's key features that MUST remain unchanged (e.g., "Specific eye shape/color", "Unique facial mole", "Exact texture of clothing material", "Text on sign reading 'XYZ'").
3. LIGHTING & ATMOSPHERE: Describe the lighting direction and color temperature.

  GOAL: Your analysis will be used by a Pro model to upscale the image while guaranteeing it looks exactly like the original subject(Identity Preservation).
       
       STRICT SAFETY RULES:
- Do NOT use terms like "watermark", "copyright", "signature", or "logo" in your output, as these trigger safety filters in the next step.
       - If you see such elements, refer to them generally as "text overlays" or "graphic patterns".
       
       Provide a structured summary.`,
      "Step 1: Defect & Fidelity Analysis",
      "preset",
      {
        outputType: 'text',
        readOnly: true,
        selectedModel: 'gemini-3-flash-preview',
        operation: 'gemini'
      }
    );

    // Step 2: High-Fidelity Upscale & Fix (Gemini 3.0 Pro)
    addStep(
      `Role: High - End Photo Retoucher(Fidelity Specialist).
  TASK: Upscale and Enhance the image to 4K resolution based on the analysis.
       
       INPUT ANALYSIS:
{ { ANALYSIS } }
       
       STRICT EXECUTION RULES:
1. IDENTITY PRESERVATION: The subject MUST look 100 % identical to the original.Do not change facial features, expression, or key details described in the analysis.
       2. DEFECT REMOVAL: Fix the defects listed(noise, blur, artifacts) WITHOUT smoothing over real textures.
       3. UPSCALING: Output a crisp, high - resolution 4K result.
       4. LIGHTING: Keep the original atmosphere.

  CRITICAL: If the image is already good, just sharpen it slightly.Do not hallucinate new features.

    OUTPUT: IMAGE ONLY.`,
      "Step 2: Pro Upscale (4K)",
      "preset",
      {
        outputType: 'image',
        readOnly: false, // User can tweak if needed
        selectedModel: 'gemini-3-pro-image-preview',
        operation: 'gemini'
      }
    );
  };

  // --- ISOLATE OBJECT HANDLER ---
  const handleAddIsolateObject = () => {
    addStep(
      `Role: Expert Digital Imaging Technician specializing in authentic product isolation.
  TASK: Isolate the MAIN SUBJECT on a pure white background with ZERO modification to the subject itself.

  CRITICAL INSTRUCTION: IDENTITY PRESERVATION IS PARAMOUNT.
  - You must act as a "Pixel-Perfect Scissors" tool.
  - Do NOT enhance, sharpen, relight, or "improve" the subject in any way.
  - Do NOT regenerate parts of the object.
  - The pixel values of the subject must remain as close to the original as possible.
       
       STRICT EXECUTION RULES:
      1. BACKGROUND: Pure #FFFFFF White. Flat and uniform.
      2. SUBJECT INTEGRITY: PRESERVE EXACT DETAILS. Texture, lighting, reflections, and imperfections must remain UNTOUCHED.
      3. EDGES: Creating a clean cutout is the ONLY allowed modification. Edges must be sharp and precise.
      4. NO HALLUCINATION: Do not add or guess details. If a part is blurry in the original, it MUST stay blurry.

 OUTPUT: IMAGE ONLY.`,
      "Isolate Object",
      "preset",
      {
        outputType: 'image',
        readOnly: false,
        selectedModel: 'gemini-2.5-flash-image', // Good default for segmentation
        operation: 'gemini',
        config: {
          backgroundColor: 'white' // Default
        }
      }
    );
  };

  // --- ALIGN OBJECT HANDLER ---
  const handleAddAlignObject = () => {
    addStep(
      `ROLE: You are a print prepress orientation evaluator.
  TASK(ANALYSIS ONLY): Analyze the object and determine rotation for optimal print efficiency.
       
       EVALUATION RULES:
       1. "BOTTOM CONTACT LINE" RULE: Imagine drawing a straight line that touches the lowest extreme points of the object across its entire width.
       2. GOAL: Rotate the object until this "Bottom Contact Line" is perfectly horizontal (0 degrees alignment with the horizon).
       3. STABILITY: The object should appear to be resting flat on this line.
       4. CALCULATION: Measure the angle of the current bottom-most points and calculate the exact rotation needed to make them level.
       5. Output the precise angle (e.g. 12.5, -4.0).
       
       OUTPUT REQUIREMENTS:
       Return ONLY valid JSON:
{
  "required": boolean,
    "angle": number, // Positive for Clockwise, Negative for Counter-Clockwise
      "reason": "string"
} `,
      "Align Object (Analysis)",
      "preset",
      {
        outputType: 'text', // We only want the JSON analysis
        readOnly: false,
        selectedModel: 'gemini-3-flash-preview', // Flash is sufficient for analysis
        operation: 'gemini'
      }
    );
  };

  const removeStep = (id: string) => {
    setWorkflowSteps(prev => prev.filter(step => step.id !== id));
    if (editingStepId === id) setEditingStepId(null);
    if (viewingStepId === id) setViewingStepId(null);
  };

  const startEditingStep = (step: WorkflowStep) => {
    if (step.readOnly) return;
    setEditingStepId(step.id);
    setEditPromptValue(step.prompt);
  };

  const saveStepPrompt = (id: string) => {
    setWorkflowSteps(prev => prev.map(s =>
      s.id === id ? { ...s, prompt: editPromptValue } : s
    ));
    setEditingStepId(null);
    setEditPromptValue('');
  };

  const cancelEditing = () => {
    setEditingStepId(null);
    setEditPromptValue('');
  };

  const handleAddCustomStep = () => {
    if (!customPrompt.trim()) return;
    addStep(customPrompt, `Custom Step`, 'custom');
    setCustomPrompt('');
  };

  // Select a step to view its result in the main preview
  const handleViewStepResult = (stepId: string | null) => {
    // Only view if it has an image result
    // Allow viewing all complete steps
    const step = workflowSteps.find(s => s.id === stepId);
    if (step && step.status === 'skipped') return;
    setViewingStepId(stepId);
  };

  const handleProcessWorkflow = async () => {
    if (!image || workflowSteps.length === 0) return;

    // Check if any step requires a paid key (Pro or Imagen)
    const requiresPaidKey = workflowSteps.some(step =>
      step.operation === 'gemini' &&
      step.selectedModel && MODELS[step.selectedModel].isPaid
    );

    if (requiresPaidKey) {
      try {
        // @ts-ignore - window.aistudio is injected
        const hasKey = await window.aistudio.hasSelectedApiKey();
        if (!hasKey) {
          // @ts-ignore
          await window.aistudio.openSelectKey();
        }
      } catch (e) {
        console.warn("AI Studio key selection check failed, proceeding anyway...", e);
      }
    }

    setStatus(AppStatus.PROCESSING);
    setError(null);
    setViewingStepId(null);

    // Reset steps to pending, keeping prompt updates
    setWorkflowSteps(prev => prev.map(s => ({
      ...s,
      status: 'pending',
      resultUrl: null,
      resultText: null,
      usage: undefined
    })));

    let currentBase64 = image.originalBase64;
    let currentMimeType = image.mimeType;
    let finalUrl: string | null = null;
    let lastAnalysisText = "";

    // We don't need 'sourceImageForMasking' in this new workflow logic
    // because we operate sequentially on the result of the previous step.

    try {
      let stepsToExecute = [...workflowSteps];

      for (let i = 0; i < stepsToExecute.length; i++) {
        const stepId = stepsToExecute[i].id;

        // Mark current step as processing
        setWorkflowSteps(prev => prev.map(s =>
          s.id === stepId ? { ...s, status: 'processing' } : s
        ));

        // Get the most up-to-date Step object
        const currentStep = stepsToExecute[i];

        if (currentStep.operation === 'gemini') {
          // --- GEMINI OPERATION ---

          let currentStepPrompt = currentStep.prompt;

          // Template replacement for {{ANALYSIS}}
          if (currentStepPrompt.includes('{{ANALYSIS}}')) {
            currentStepPrompt = currentStepPrompt.replace('{{ANALYSIS}}', lastAnalysisText || "No analysis provided.");
          }

          // CONFIGURATION FOR PRO MODEL (Fix file size/resolution)
          let config: any = {};
          if (currentStep.selectedModel === 'gemini-3-pro-image-preview') {
            // Force 4K resolution for Pro model to avoid small 500kb files
            config = {
              imageConfig: {
                imageSize: '4K'
              }
            };
          }

          // BACKGROUND COLOR CONFIGURATION
          if (currentStep.config?.backgroundColor) {
            const color = currentStep.config.backgroundColor;
            let bgDesc = "a flat, solid #FFFFFF white";
            if (color === 'black') bgDesc = "a flat, solid #000000 black";
            if (color === 'blue') bgDesc = "a flat, solid #0000FF blue";
            if (color === 'green') bgDesc = "a flat, solid #00FF00 green";
            if (color === 'transparent') bgDesc = "100% transparent alpha channel (PNG)";

            currentStepPrompt = currentStepPrompt.replace('{{BG_COLOR}}', color.toUpperCase());
            currentStepPrompt = currentStepPrompt.replace('{{BG_DESC}}', bgDesc);
          }

          const result = await editImageWithGemini(
            currentBase64,
            currentMimeType,
            currentStepPrompt,
            currentStep.outputType || 'image',
            currentStep.selectedModel,
            config
          );

          if (currentStep.outputType === 'text') {
            const textResult = result.text || "No text returned";
            lastAnalysisText = textResult;

            // SPECIAL HANDLING FOR ALIGN OBJECT (JSON ANALYSIS -> CLIENT ROTATION)
            let finalResultUrl: string | null = null;

            if (currentStep.label.includes("Align Object")) {
              try {
                // Clean markdown if present
                const cleanJson = textResult.replace(new RegExp("```json", "g"), '').replace(new RegExp("```", "g"), '').trim();
                const analysis = JSON.parse(cleanJson);

                if (analysis) {
                  const angle = analysis.angle || 0;
                  console.log(`Auto-rotating/Trimming image by ${angle} degrees based on analysis.`);
                  // Perform rotation on the CURRENT image (input for this step)
                  // FORCE JPEG OUTPUT directly in the rotation call
                  const rotatedBase64 = await rotateImage(currentBase64, angle, 'image/jpeg');

                  // Update the "result" of this step to be the rotated image
                  // Even though outputType was text, we enhance it with a visual result
                  finalResultUrl = rotatedBase64;
                  console.log("finalResultUrl", finalResultUrl);
                }
              } catch (e) {
                console.error("Failed to apply auto-rotation:", e);
              }
            }

            setWorkflowSteps(prev => prev.map(s =>
              s.id === stepId ? {
                ...s,
                status: 'complete',
                resultText: finalResultUrl ? textResult : (textResult + "\n\n[System Error]: Rotation logic failed or was not required."),
                resultUrl: finalResultUrl, // Attach image if we produced one
                usage: result.usage
              } : s
            ));

            if (finalResultUrl) {
              setImage(prev => prev ? { ...prev, processedUrl: finalResultUrl } : null);

              // CRITICAL FIX: Update the loop variable so subsequent steps (if any) use the rotated image
              // Extract pure base64 from the data URL
              const base64Data = finalResultUrl.split(',')[1];
              if (base64Data) {
                currentBase64 = base64Data;
                currentMimeType = 'image/jpeg'; // FIX: Rotation always returns JPEG
                finalUrl = finalResultUrl; // FIX: Ensure the outer finalUrl acts as the "latest" for the post-loop update
              }
            }

          } else {
            currentBase64 = result.base64!;
            currentMimeType = result.mimeType!;
            const stepResultUrl = `data:${currentMimeType};base64,${currentBase64}`;

            // Should valid text be returned with image, save it too
            const resultText = result.text || null;

            setWorkflowSteps(prev => prev.map(s =>
              s.id === stepId ? { ...s, status: 'complete', resultUrl: stepResultUrl, resultText: resultText, usage: result.usage } : s
            ));
            setImage(prev => prev ? { ...prev, processedUrl: stepResultUrl } : null);
            setViewingStepId(stepId);
          }
        }

      }

      finalUrl = `data:${currentMimeType};base64,${currentBase64}`;
      setImage(prev => prev ? { ...prev, processedUrl: finalUrl } : null);
      setStatus(AppStatus.COMPLETE);

    } catch (err: any) {
      setWorkflowSteps(prev => prev.map(s =>
        s.status === 'processing' ? { ...s, status: 'error' } : s
      ));
      setError({
        message: 'Workflow failed',
        details: err.message
      });
      setStatus(AppStatus.ERROR);
    }
  };

  const handleDownload = () => {
    const urlToDownload = viewingStepId
      ? workflowSteps.find(s => s.id === viewingStepId)?.resultUrl
      : image?.processedUrl;

    if (urlToDownload) {
      const link = document.createElement('a');
      link.href = urlToDownload;
      link.download = `edited-workflow-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleReset = () => {
    setImage(null);
    setWorkflowSteps([]);
    setCustomPrompt('');
    setStatus(AppStatus.IDLE);
    setError(null);
    setViewingStepId(null);
  };

  const currentDisplayedUrl = viewingStepId
    ? workflowSteps.find(s => s.id === viewingStepId)?.resultUrl || image?.processedUrl
    : image?.processedUrl;

  return (
    <div className="min-h-screen flex flex-col font-sans gemini-app-scope">

      <main className="flex-grow p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl mx-auto space-y-8 animate-fade-in">

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

            {/* LEFT COLUMN: Source Image or Uploader */}
            <div className="lg:col-span-4 space-y-4">
              <div className="sticky top-24 space-y-4">
                {image ? (
                  <div className="rounded-xl overflow-hidden border border-slate-700 bg-slate-900 shadow-lg relative aspect-square flex items-center justify-center animate-fade-in">
                    <img
                      src={image.originalUrl}
                      alt="Original Preview"
                      className="max-w-full max-h-full object-contain"
                    />
                    <div className="absolute top-2 right-2">
                      <button
                        onClick={handleReset}
                        className="bg-black/50 hover:bg-black/70 text-white p-2 rounded-full backdrop-blur-sm transition-colors"
                        title="Change Image"
                        disabled={status === AppStatus.PROCESSING}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="aspect-square animate-fade-in">
                    <ImageUploader onImageSelected={handleImageSelected} className="h-full" />
                  </div>
                )}

                <div className="text-xs text-center text-slate-500 uppercase tracking-widest font-semibold">
                  Source Image
                </div>
              </div>
            </div>

            {/* MIDDLE COLUMN: Workflow Queue */}
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 flex flex-col h-full max-h-[800px]">
                <div className="p-4 border-b border-slate-700 bg-slate-800/80 rounded-t-xl">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <Wand2 size={18} className="text-brand-400" />
                    Workflow Queue
                  </h3>
                  <p className="text-xs text-slate-400 mt-1">
                    Configure steps. Edit prompts before running.
                  </p>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px]">
                  {workflowSteps.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-slate-500 border-2 border-dashed border-slate-700 rounded-lg p-6">
                      <Plus size={32} className="mb-2 opacity-50" />
                      <p className="text-sm">Add steps from the right panel</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {workflowSteps.map((step:any, index) => (
                        <div key={step.id} className="relative group">
                          {index < workflowSteps.length - 1 && (
                            <div className="absolute left-6 -bottom-4 w-px h-4 bg-slate-700 z-0"></div>
                          )}

                          <div className={`
                              relative z-10 p-3 rounded-lg border flex flex-col gap-2 transition-all
                              ${viewingStepId === step.id ? 'ring-2 ring-brand-500 border-transparent bg-slate-700' : ''}
                              ${step.status === 'processing' ? 'bg-brand-900/20 border-brand-500/50' :
                              step.status === 'complete' ? 'bg-slate-800 border-green-500/30' :
                                step.status === 'error' ? 'bg-red-900/10 border-red-500/30' :
                                  step.status === 'skipped' ? 'bg-slate-800/50 border-slate-700 border-dashed opacity-60' :
                                    'bg-slate-800 border-slate-700'}
                            `}>
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-3 overflow-hidden flex-1">
                                <div
                                  className={`
                                      w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 cursor-pointer
                                      ${step.status === 'processing' ? 'bg-brand-500 text-white animate-pulse' :
                                      step.status === 'complete' ? 'bg-green-500 text-white' :
                                        step.status === 'skipped' ? 'bg-slate-700 text-slate-500' :
                                          'bg-slate-600 text-slate-300'}
                                    `}
                                  onClick={() => step.status === 'complete' && handleViewStepResult(step.id)}
                                >
                                  {step.status === 'complete' ? <Check size={14} /> :
                                    step.status === 'skipped' ? <FastForward size={14} /> : index + 1}
                                </div>

                                <div className="truncate flex-1">
                                  <div className="text-sm font-medium text-slate-200 truncate flex items-center gap-2">
                                    {step.label}
                                    {step.readOnly && <Lock size={12} className="text-slate-500" />}
                                  </div>
                                  <div className="text-xs text-slate-500 truncate flex items-center gap-2">
                                    {step.type === 'preset' ? 'Preset' : 'Custom Prompt'}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center gap-1">
                                {step.status === 'pending' && editingStepId !== step.id && (
                                  <>
                                    <button
                                      onClick={() => startEditingStep(step)}
                                      className={`
                                            p-1.5 rounded transition-colors
                                            ${step.readOnly ? 'text-slate-600 cursor-not-allowed' : 'text-slate-500 hover:text-brand-400 hover:bg-slate-700'}
                                          `}
                                      title={step.readOnly ? "Locked" : "Edit Prompt"}
                                      disabled={status === AppStatus.PROCESSING || !!step.readOnly}
                                    >
                                      <Pencil size={14} />
                                    </button>
                                    <button
                                      onClick={() => removeStep(step.id)}
                                      className="text-slate-500 hover:text-red-400 p-1.5 hover:bg-slate-700 rounded transition-colors"
                                      title="Remove Step"
                                      disabled={status === AppStatus.PROCESSING}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </>
                                )}

                                {step.status === 'complete' && step.resultUrl && (
                                  <button
                                    onClick={() => handleViewStepResult(step.id)}
                                    className={`
                                          p-1.5 rounded transition-colors flex items-center gap-1 text-xs font-medium
                                          ${viewingStepId === step.id && (!step.resultText || viewingStepId === step.id) // Simplification
                                        ? 'bg-brand-500 text-white'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-700'}
                                        `}
                                    title="View Result"
                                  >
                                    <Eye size={14} />
                                    <span className="hidden sm:inline">View</span>
                                  </button>
                                )}

                                {(step.outputType === 'text' || step.resultText) && step.status === 'complete' && (
                                  <button
                                    onClick={() => setViewingStepId(step.id)} // View text
                                    className={`
                                          p-1.5 rounded transition-colors flex items-center gap-1 text-xs font-medium
                                          ${viewingStepId === step.id // TODO: Distinguish view modes if needed, currently sharing view slot
                                        ? 'text-brand-400'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-700'}
                                        `}
                                    title="View Report"
                                  >
                                    <FileText size={14} />
                                    <span className="hidden sm:inline">Report</span>
                                  </button>
                                )}

                                {step.status === 'processing' && <span className="animate-spin text-brand-400">⟳</span>}
                                {step.status === 'skipped' && <span className="text-slate-500 text-[10px] uppercase">Skipped</span>}
                              </div>
                            </div>

                            {/* --- Step Config Area --- */}
                            {step.operation === 'client-trim' && step.status === 'pending' && (
                              <div className="mt-2 pl-9">
                                <label className="text-[10px] text-slate-400 flex items-center gap-2">
                                  Padding (px):
                                  <input
                                    type="number"
                                    value={step.config?.padding ?? 5}
                                    onChange={(e) => updateStepConfig(step.id, 'padding', parseInt(e.target.value) || 0)}
                                    className="bg-slate-900 border border-slate-700 rounded w-16 px-1 py-0.5 text-xs text-white"
                                  />
                                </label>
                              </div>
                            )}

                            {step.operation === 'client-resize' && step.status === 'pending' && (
                              <div className="mt-2 pl-9">
                                <label className="text-[10px] text-slate-400 flex items-center gap-2">
                                  Max Width (px):
                                  <input
                                    type="number"
                                    value={step.config?.maxWidth ?? 2048}
                                    onChange={(e) => updateStepConfig(step.id, 'maxWidth', parseInt(e.target.value) || 2048)}
                                    className="bg-slate-900 border border-slate-700 rounded w-16 px-1 py-0.5 text-xs text-white"
                                  />
                                </label>
                              </div>
                            )}

                            {/* Background Color Config */}
                            {step.operation === 'gemini' && step.config?.backgroundColor && (
                              <div className="mt-2 pl-9 animate-fade-in">
                                <label className="text-[10px] text-slate-400 flex items-center gap-2">
                                  Background:
                                  <select
                                    value={step.config.backgroundColor}
                                    onChange={(e) => updateStepConfig(step.id, 'backgroundColor', e.target.value)}
                                    disabled={status === AppStatus.PROCESSING || step.readOnly}
                                    className="bg-slate-900 border border-slate-700 rounded w-24 px-1 py-0.5 text-xs text-white focus:outline-none focus:border-brand-500"
                                  >
                                    <option value="white">White</option>
                                    <option value="black">Black</option>
                                    <option value="green">Green</option>
                                    <option value="blue">Blue</option>
                                  </select>
                                </label>
                              </div>
                            )}

                            {/* Model Selector (AI Only) */}
                            {step.status === 'pending' && step.outputType !== 'text' && step.operation === 'gemini' && (
                              <div className="mt-2 pl-9">
                                <div className="relative">
                                  <select
                                    value={step.selectedModel || 'gemini-2.5-flash-image'}
                                    onChange={(e) => changeStepModel(step.id, e.target.value as ModelId)}
                                    className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-300 rounded px-2 py-1.5 appearance-none cursor-pointer hover:border-slate-500 focus:outline-none focus:border-brand-500 pr-8"
                                    disabled={status === AppStatus.PROCESSING}
                                  >
                                    {Object.entries(MODELS)
                                      .filter(([key]) => key !== 'gemini-3-flash-preview') // Hide text-only model from image selector
                                      .map(([key, model]) => (
                                        <option key={key} value={key}>
                                          {model.label} {model.isPaid ? '($$$)' : ''}
                                        </option>
                                      ))}
                                  </select>
                                  <Settings2 size={12} className="absolute right-2 top-2 text-slate-500 pointer-events-none" />
                                </div>
                                {MODELS[step.selectedModel || 'gemini-2.5-flash-image']?.isPaid && (
                                  <div className="text-[10px] text-yellow-500 mt-1 flex items-center gap-1">
                                    <Info size={10} />
                                    Requires Paid API Key Selection
                                  </div>
                                )}
                              </div>
                            )}

                            {(step.status !== 'pending' && step.outputType !== 'text' && step.operation === 'gemini') && step.selectedModel && step.status !== 'skipped' && (
                              <div className="mt-1 pl-9 text-[10px] text-slate-500 flex items-center gap-1">
                                <span className={`w-1.5 h-1.5 rounded-full ${MODELS[step.selectedModel].isPaid ? 'bg-purple-500' : 'bg-green-500'}`}></span>
                                {MODELS[step.selectedModel].label}
                              </div>
                            )}

                            {step.operation === 'client-flip' && (
                              <div className="mt-1 pl-9 text-[10px] text-brand-400 flex items-center gap-1">
                                <span>Local Canvas Operation (Free)</span>
                              </div>
                            )}

                            {step.operation === 'remove-white-bg' && (
                              <div className="mt-1 pl-9 text-[10px] text-brand-400 flex items-center gap-1">
                                <span>Local Flood Fill (Free)</span>
                              </div>
                            )}

                            {step.operation === 'client-trim' && step.status === 'complete' && (
                              <div className="mt-1 pl-9 text-[10px] text-brand-400 flex items-center gap-1">
                                <span>Trimmed with {step.config?.padding ?? 5}px padding</span>
                              </div>
                            )}

                            {step.operation === 'client-rotate' && step.status === 'complete' && (
                              <div className="mt-1 pl-9 text-[10px] text-brand-400 flex items-center gap-1">
                                <span>Auto-Aligned using Moments</span>
                              </div>
                            )}

                            {step.operation === 'client-resize' && step.status === 'complete' && (
                              <div className="mt-1 pl-9 text-[10px] text-brand-400 flex items-center gap-1">
                                <span>Web Optimized (Max {step.config?.maxWidth}px)</span>
                              </div>
                            )}

                            {editingStepId === step.id ? (
                              <div className="mt-2 pl-9 animate-fade-in">
                                <textarea
                                  value={editPromptValue}
                                  onChange={(e) => setEditPromptValue(e.target.value)}
                                  className="w-full bg-slate-900 border border-slate-600 rounded-md p-2 text-xs text-slate-200 focus:ring-1 focus:ring-brand-500 focus:border-brand-500 outline-none min-h-[60px]"
                                  autoFocus
                                />
                                <div className="flex justify-end gap-2 mt-2">
                                  <button
                                    onClick={cancelEditing}
                                    className="text-xs text-slate-400 hover:text-white px-2 py-1"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => saveStepPrompt(step.id)}
                                    className="text-xs bg-brand-600 hover:bg-brand-500 text-white px-3 py-1 rounded"
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="pl-9 mt-1">
                                <div className="text-xs text-slate-400 truncate opacity-70">
                                  {(step.operation === 'gemini') ?
                                    (step.prompt.includes('TEMPLATE_STEP') ? "Waiting for audit result..." : `"${step.prompt.substring(0, 100)}${step.prompt.length > 100 ? '...' : ''}"`)
                                    : '---'
                                  }
                                </div>
                                {step.resultText && (
                                  <div className="mt-2 text-xs bg-black/40 p-2 rounded border border-slate-700 text-slate-300 max-h-[80px] overflow-y-auto font-mono">
                                    {step.resultText}
                                  </div>
                                )}
                              </div>
                            )}

                            {step.status === 'complete' && showCost && (
                              <div className="mt-2 pt-2 border-t border-slate-700/50 flex flex-wrap items-center justify-between text-[10px] text-slate-500 gap-y-1 animate-fade-in">
                                <div className="flex gap-2">
                                  {step.usage ? (
                                    <>
                                      <span title="Prompt Tokens">In: {step.usage.promptTokens.toLocaleString()}</span>
                                      {step.outputType === 'image' ? (
                                        <span title="Generated Image">Out: 1 Image</span>
                                      ) : (
                                        <span title="Candidate Tokens">Out: {step.usage.candidateTokens.toLocaleString()}</span>
                                      )}
                                    </>
                                  ) : (step.operation !== 'gemini') ? (
                                    <span>Local CPU</span>
                                  ) : (
                                    <span>Flat Rate</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-slate-400">${getStepCost(step)?.toFixed(4)}</span>
                                </div>
                              </div>
                            )}

                            {step.status === 'complete' && step.outputType === 'image' && step.resultUrl && (
                              <div className="mt-3 pl-9">
                                <div className="text-[10px] text-slate-500 mb-1 uppercase tracking-wider font-semibold">Step Result:</div>
                                <div
                                  className={`
                                      relative group w-full aspect-video bg-black/50 rounded-lg border border-slate-700 overflow-hidden cursor-pointer transition-all
                                      ${viewingStepId === step.id ? 'ring-2 ring-brand-400 border-transparent' : 'hover:border-slate-500'}
                                    `}
                                  onClick={() => handleViewStepResult(step.id)}
                                >
                                  <img
                                    src={step.resultUrl}
                                    className="w-full h-full object-contain"
                                    alt="step result"
                                  />
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <span className="flex items-center gap-2 text-white font-medium text-xs bg-black/60 px-3 py-1.5 rounded-full backdrop-blur-sm border border-white/10">
                                      <Eye size={14} /> Preview Full
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      <div ref={queueEndRef} />
                    </div>
                  )}
                </div>

                <div className="p-4 border-t border-slate-700 bg-slate-800/80 rounded-b-xl space-y-3">
                  {(status === AppStatus.COMPLETE || workflowSteps.some(s => s.status === 'complete')) && (
                    <div className="flex items-center justify-end mb-1">
                      <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-400 hover:text-slate-300 select-none group">
                        <span>Show Usage & Cost</span>
                        <div className="relative">
                          <input type="checkbox" className="sr-only" checked={showCost} onChange={() => setShowCost(!showCost)} />
                          <div className={`w-8 h-4 rounded-full transition-colors ${showCost ? 'bg-brand-600' : 'bg-slate-700 group-hover:bg-slate-600'}`}></div>
                          <div className={`absolute left-0.5 top-0.5 bg-white w-3 h-3 rounded-full transition-transform shadow-sm ${showCost ? 'translate-x-4' : 'translate-x-0'}`}></div>
                        </div>
                      </label>
                    </div>
                  )}

                  {(status === AppStatus.COMPLETE || workflowSteps.some(s => s.status === 'complete')) && showCost && (
                    <div className="flex flex-col gap-2 p-3 bg-slate-900/50 rounded border border-slate-700/50 text-xs text-slate-400 animate-fade-in">
                      <div className="flex items-center justify-between border-b border-slate-800 pb-2 mb-1">
                        <span className="font-semibold text-slate-300 flex items-center gap-2">
                          <Coins size={12} className="text-brand-400" />
                          Session Cost
                        </span>
                        <span className="font-mono text-white text-sm">{stats.totalCost}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[10px] opacity-80">
                        <div className="flex justify-between">
                          <span>Input Tokens:</span>
                          <span className="font-mono">{stats.totalPromptTokens.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Output Tokens:</span>
                          <span className="font-mono">{stats.totalCandidateTokens.toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="text-[9px] text-slate-600 mt-1 text-center">
                        Rates vary by selected model
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-2 text-xs">
                      <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={14} />
                      <div className="text-red-200">
                        <span className="font-semibold block text-red-400">Failed at Step {workflowSteps.findIndex(s => s.status === 'error') + 1}</span>
                        {error.message}
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleProcessWorkflow}
                    disabled={!image || workflowSteps.length === 0 || status === AppStatus.PROCESSING}
                    isLoading={status === AppStatus.PROCESSING}
                    className="w-full"
                    icon={<Play size={18} className={status === AppStatus.PROCESSING ? 'hidden' : ''} />}
                  >
                    {status === AppStatus.PROCESSING ? 'Processing Workflow...' : 'Run Workflow'}
                  </Button>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: Controls */}
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-slate-800/30 p-4 rounded-xl border border-slate-700/50">
                <h4 className="text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wider">Add Custom Step</h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddCustomStep()}
                    placeholder="e.g. Add a red hat"
                    className="flex-1 bg-slate-900 text-white border-slate-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    disabled={status === AppStatus.PROCESSING}
                  />

                  <Button
                    onClick={handleAddCustomStep}
                    disabled={!customPrompt.trim() || status === AppStatus.PROCESSING}
                    variant="secondary"
                    className="px-3"
                  >
                    <Plus size={18} />
                  </Button>
                </div>
              </div>

              <div className="bg-slate-800/30 p-4 rounded-xl border border-slate-700/50">
                <h4 className="text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wider">Preset Steps</h4>
                <div className="space-y-2">
                  {PRESET_PROMPTS.map((preset) => (
                    <div key={preset.id} className="w-full">
                      <button
                        onClick={() => {
                          if (preset.id === 'smart-enhance') {
                            handleAddSmartEnhancement();
                          } else if (preset.id === 'isolate-object') {
                            handleAddIsolateObject();
                          } else if (preset.id === 'align-object') {
                            handleAddAlignObject();
                          } else {
                            addStep(preset.prompt, preset.label, 'preset', {
                              selectedModel: 'gemini-2.5-flash-image'
                            });
                          }
                        }}
                        disabled={status === AppStatus.PROCESSING}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-slate-700/30 hover:bg-slate-700 border border-slate-600/30 hover:border-brand-500/50 transition-all text-left group"
                      >
                        <div className="text-slate-400 group-hover:text-brand-400 bg-slate-800 p-2 rounded-md">
                          {preset.icon}
                        </div>
                        <div>
                          <div className="font-medium text-slate-200 text-sm group-hover:text-white">{preset.label}</div>
                          <div className="text-xs text-slate-500 truncate max-w-[200px]">{preset.description}</div>
                        </div>
                        <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                          <Plus size={16} className="text-brand-400" />
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {viewingStepId && workflowSteps.find(s => s.id === viewingStepId)?.outputType === 'text' ? (
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 shadow-2xl h-full max-h-[800px] flex flex-col">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-800">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <FileText className="text-brand-400" />
                  Analysis Report
                </h3>
                <button
                  onClick={() => setViewingStepId(null)}
                  className="text-slate-400 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                <pre className="text-slate-300 font-mono text-sm whitespace-pre-wrap leading-relaxed">
                  {workflowSteps.find(s => s.id === viewingStepId)?.resultText || "No text content."}
                </pre>
              </div>
            </div>
          ) : (
            (status === AppStatus.COMPLETE || (viewingStepId && workflowSteps.find(s => s.id === viewingStepId)?.outputType === 'image')) && currentDisplayedUrl && image && (
              <ComparisonView
                originalUrl={image.originalUrl}
                processedUrl={currentDisplayedUrl}
                onDownload={handleDownload}
                onReset={handleReset}
              />
            )
          )}

        </div>
      </main>
    </div>
  );
};

export default App;